import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { HttpError } from '@magicdoorinc/errors';
import { useLocalization, toast } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal, For } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { getOwnerName } from '~/components/owner/utils';
import { useBankAccountsList } from '~/contexts/bank-accounts';
import { buildProcessedBills, processBillsResponse } from '~/contexts/bills/payVendorBillsData';
import { computeFundingByProperty } from '~/contexts/bills/payVendorBillsFunding';
import { createMagicDoorContext } from '~/contexts/utils';
import { useVendorBillsByMode, groupBy } from '~/hooks/vendorBills';
import { ChartOfAccountsRepository } from '~/repositories/chartOfAccountsRepository';
import { PropertiesRepository } from '~/repositories/propertiesRepository';
import { buildBankAccountOptions, getCompanyAccountIds } from '~/utils/bankAccountHelpers';
import { uuid } from '~/utils/tool';
import type { GetVendorBillsToPayParams } from '~/swagger/portal/Api';
import type { BillPayment, BillPaymentSection, PayVendorBillsBankAccountOption, PayVendorBillsStore } from '~/types/payVendorBills';
export type { BillPayment } from '~/types/payVendorBills';

export interface PaymentRow {
  section: BillPaymentSection;
  bills: BillPayment[];
  vendorName: string;
  totalAmount: number;
}

const initialStore = (): PayVendorBillsStore => ({
  submitLoading: false,
  propertiesLoading: false,
  chartOfAccountOptions: [],
  chartOfAccountMap: new Map(),
  chartOfAccountLoading: false,
  chartOfAccounts: [],
  bills: [],
  billsLoading: false,
  bankAccountOptions: [],
  bankAccounts: [],
  bankAccountsLoading: false,
  billPayments: [],
  paymentDate: todayWithSystemTimeZone().format('YYYY-MM-DD'),
  billPaymentSections: [],
});

export function createManualBillPaymentSection(vendorId: string, billIds: string[] = []): BillPaymentSection {
  return {
    sectionId: uuid(),
    vendorId,
    billIds,
    isCompanyBill: false,
    isManualAdded: true,
  };
}

export function buildBillPaymentSections(billPayments: BillPayment[]): BillPaymentSection[] {
  const sections: BillPaymentSection[] = [];

  for (const item of billPayments.filter((bill) => !bill.excluded)) {
    const foundIndex = sections.findIndex(
      (s) =>
        s.vendorId === item.vendor.id &&
        s.propertyDefaultBankAccountId === item.paymentBankAccountId &&
        s.isCompanyBill === item.companyBill
    );

    if (foundIndex < 0) {
      sections.push({
        sectionId: uuid(),
        vendorId: item.vendor.id,
        billIds: [item.id],
        propertyDefaultBankAccountId: item.paymentBankAccountId,
        isCompanyBill: item.companyBill,
        isManualAdded: false,
      });
    } else {
      sections[foundIndex].billIds.push(item.id);
    }
  }

  return sections;
}

export const [PayVendorBillsProvider, usePayVendorBills] = createMagicDoorContext('PayVendorBills', () => {
  const chartOfAccountsRepo = new ChartOfAccountsRepository();
  const propertiesRepo = new PropertiesRepository();

  const { t } = useLocalization();
  const { bankAccounts: globalBankAccounts } = useBankAccountsList();
  const { fetchBills: fetchBillsFromHook, payBills: payBillsFromHook } = useVendorBillsByMode();
  const [store, setStore] = createStore<PayVendorBillsStore>(initialStore());
  const bankAccountOptions = createMemo<PayVendorBillsBankAccountOption[]>(() => buildBankAccountOptions(globalBankAccounts()));
  const companyAccountIds = createMemo(() => getCompanyAccountIds(globalBankAccounts()));

  createEffect(() => {
    setStore({
      bankAccounts: globalBankAccounts() ?? [],
      bankAccountOptions: bankAccountOptions(),
      bankAccountsLoading: globalBankAccounts.loading,
    });
  });

  const fetchChartOfAccounts = async () => {
    setStore('chartOfAccountLoading', true);
    try {
      const response = await chartOfAccountsRepo.getChartOfAccounts();
      const accounts = response ?? [];
      const options = accounts.map((a) => ({ label: a.name, value: a.id }));
      const accountMap = new Map(accounts.map((a) => [a.id, a.name]));

      setStore('chartOfAccountOptions', options);
      setStore('chartOfAccountMap', accountMap);
      setStore('chartOfAccounts', accounts);
    } finally {
      setStore('chartOfAccountLoading', false);
    }
  };

  const initBillPaymentsAndSections = (billPayments: BillPayment[]) => {
    setStore('billPayments', billPayments);
    setStore('billPaymentSections', buildBillPaymentSections(billPayments));
  };

  const [propertyOwnerNames, setPropertyOwnerNames] = createSignal<Map<string, string>>(new Map());
  const [propertyOwnerIds, setPropertyOwnerIds] = createSignal<Map<string, string[]>>(new Map());

  const fetchPropertyOwnerNames = async (billPayments: BillPayment[]) => {
    const propertyIds = Array.from(new Set(billPayments.map((bill) => bill.property?.id).filter((id): id is string => Boolean(id))));

    if (!propertyIds.length) {
      setPropertyOwnerNames(new Map());
      setPropertyOwnerIds(new Map());
      return;
    }

    try {
      const properties = await propertiesRepo.batchGetPropertyByIds(propertyIds);
      setPropertyOwnerNames(
        new Map(
          properties.map((property) => [
            property.id,
            property.owners
              .map((item) => getOwnerName(item.owner))
              .filter(Boolean)
              .join(', '),
          ])
        )
      );
      setPropertyOwnerIds(
        new Map(
          properties.map((property) => [
            property.id,
            property.owners.map((item) => item.owner?.id).filter((id): id is string => Boolean(id)),
          ])
        )
      );
    } catch (error) {
      console.error('Error fetching property owners:', error);
      setPropertyOwnerNames(new Map());
      setPropertyOwnerIds(new Map());
    }
  };

  const fetchBills = async (filter?: GetVendorBillsToPayParams): Promise<void> => {
    setStore('billsLoading', true);
    try {
      const response = await fetchBillsFromHook(filter);

      if (!response?.properties || !Array.isArray(response.properties) || !response.vendors || !Array.isArray(response.vendors)) {
        console.error("Invalid response structure. Expected 'properties' and 'vendors' arrays.");
        setStore('bills', []);
        initBillPaymentsAndSections([]);
        return;
      }

      const billPayments = processBillsResponse(response, store.chartOfAccounts, companyAccountIds(), bankAccountOptions(), t);

      const processedBills = buildProcessedBills(response);

      setStore('bills', processedBills);
      initBillPaymentsAndSections(billPayments);
      await fetchPropertyOwnerNames(billPayments);
    } catch (error) {
      console.error('Error fetching bills:', error);
      setStore('bills', []);
      initBillPaymentsAndSections([]);
    } finally {
      setStore('billsLoading', false);
    }
  };

  const rebuildBillPaymentSections = () => setStore('billPaymentSections', buildBillPaymentSections(store.billPayments));

  const totalPaymentAmount = createMemo(() =>
    store.billPayments.reduce((acc, cur) => acc + (cur.excluded ? 0 : (cur.paymentAmount ?? 0)), 0)
  );

  const billsByProperty = createMemo(() => {
    const groups = Array.from(groupBy(store.billPayments, (item) => item.property?.id).values());

    return groups.sort((left, right) => {
      const leftIsCompany = !left.at(0)?.property;
      const rightIsCompany = !right.at(0)?.property;

      if (leftIsCompany === rightIsCompany) return 0;
      return leftIsCompany ? -1 : 1;
    });
  });

  const fundingByProperty = createMemo(() => computeFundingByProperty(billsByProperty()));

  const toggleBillExcluded = (billId: string) => {
    setStore(
      produce((state) => {
        const bill = state.billPayments.find((item) => item.id === billId);
        if (bill) bill.excluded = !bill.excluded;
      })
    );
  };

  const setAllBillsExcluded = (excluded: boolean, billIds?: string[]) => {
    setStore(
      produce((state) => {
        for (const bill of state.billPayments) {
          if (billIds && !billIds.includes(bill.id)) continue;
          bill.excluded = excluded;
        }
      })
    );
  };

  const updatePaymentBillField = (payload: {
    key: keyof BillPayment;
    value: BillPayment[keyof BillPayment];
    billId?: string;
    vendorId?: string;
  }) => {
    const { key, value, vendorId, billId } = payload;
    setStore(
      produce((state) => {
        for (let i = 0; i < state.billPayments.length; i++) {
          const bill = state.billPayments[i];
          const matchByVendor = vendorId && bill?.vendor.id === vendorId;
          const matchByBill = billId && bill?.id === billId;
          if (matchByVendor || matchByBill) {
            (state.billPayments[i] as unknown as Record<string, unknown>)[key as string] = value;
            if (matchByBill) break;
          }
        }
      })
    );
  };

  const paymentSetDate = (date: string | undefined) => setStore('paymentDate', date);

  const paymentRows = createMemo<PaymentRow[]>(() =>
    store.billPaymentSections.map((section) => {
      const bills = store.billPayments.filter((bill) => !bill.excluded && section.billIds.includes(bill.id));
      return {
        section,
        bills,
        vendorName: bills.at(0)?.vendor.name ?? '',
        totalAmount: bills.reduce((acc, bill) => acc + (bill.paymentAmount ?? 0), 0),
      };
    })
  );

  const updateBillPayments = (data: { vendorId?: string; billIds: string[]; payload: Partial<BillPayment> }) => {
    const { vendorId, billIds, payload } = data;
    setStore(
      produce((state) => {
        const indexes = state.billPayments
          .map((bill, index) => {
            const matchVendor = !vendorId || bill.vendor.id === vendorId;
            const matchBills = !billIds?.length || billIds.includes(bill.id);
            return matchVendor && matchBills ? index : -1;
          })
          .filter((i) => i >= 0);

        for (const i of indexes) {
          if (state.billPayments[i]) {
            Object.assign(state.billPayments[i], payload);
          }
        }
      })
    );
  };

  const payBills = async () => {
    try {
      const storeData = {
        billPayments: store.billPayments,
        billPaymentSections: store.billPaymentSections,
        paymentDate: store.paymentDate,
        bankAccounts: store.bankAccounts,
      };
      const result = await payBillsFromHook(storeData);
      setStore('payVendorBillsResult', result);
      toast.success(t('Bills have been paid successfully'));
    } catch (e) {
      const errors = e instanceof HttpError && e.getErrors();
      const message = errors ? (
        <For each={Object.values(errors).flat()}>{(error) => <p>{error as string}</p>}</For>
      ) : (
        t('Something went wrong. Please try again.')
      );
      toast.error(message);
      throw e;
    }
  };

  return {
    store,
    setStore,
    fetchBills,
    fetchChartOfAccounts,
    payBills,
    updateBillPayments,
    updatePaymentBillField,
    rebuildBillPaymentSections,
    propertyOwnerNames,
    propertyOwnerIds,
    totalPaymentAmount,
    billsByProperty,
    fundingByProperty,
    toggleBillExcluded,
    setAllBillsExcluded,
    paymentSetDate,
    paymentRows,
  };
});
